import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { getPaginatedEvents } from '../../api/eventsAPI';
import { getAssetDashboardSummary } from '../../api/assetAPI';
import { Icon } from '@iconify/react';
import { formatIntDateTime, parseUtc } from '../../utils/dateUtils';
import { useSetting } from '../../utils/AppSettingsContext';
import { useModule } from '../../utils/useModule';

const getDownHours = (startDate, now) => {
  const start = parseUtc(startDate);
  if (Number.isNaN(start.getTime())) return '—';
  const diffMs = now.getTime() - start.getTime();
  if (diffMs <= 0) return '0.00';
  const hours = diffMs / 3_600_000;
  return hours.toFixed(1);
};

const formatDateTimeOrDash = (value) => {
  const formatted = formatIntDateTime(value);
  return formatted || '—';
};

const placeholder = `${process.env.REACT_APP_SPACES_BASE}/placeholder.png`;
const RADIAL_RADIUS = 35;
const RADIAL_CIRCUMFERENCE = 2 * Math.PI * RADIAL_RADIUS;

const DowntimeDashboardLayer = () => {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [metricsError, setMetricsError] = useState(null);
  const [timestamp, setTimestamp] = useState(() => new Date());
  const [pageIndex, setPageIndex] = useState(0);
  const [assetSummary, setAssetSummary] = useState({
    totalAssets: 0,
    onHire: 0,
    offHire: 0,
    totalDownCount: 0,
  });
  const PAGE_SIZE = 500;
  const REFRESH_INTERVAL_MS = (useSetting('dashboard_refresh_seconds') ?? 60) * 1000 || 60_000;
  const maxPageRecordsSetting = useSetting('dashboard_records_per_page');
  const planthireEnabled = useModule('planthire');

  const maxPageRecords = useMemo(() => {
    const parsed = Number(maxPageRecordsSetting);
    if (Number.isFinite(parsed) && parsed > 0) {
      return parsed;
    }

    return PAGE_SIZE;
  }, [maxPageRecordsSetting]);

  const loadEvents = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await getPaginatedEvents({
        page: 1,
        pageSize: PAGE_SIZE,
        eventStatusId: [1],
        sortField: 'startDate',
        sortOrder: 'asc',
      });
      const rows = Array.isArray(response?.data) ? response.data : [];
      setEvents(rows);
    } catch (err) {
      console.error('Failed to load downtime events', err);
      setError('Unable to load downtime events right now.');
    } finally {
      setLoading(false);
    }
  }, []);

  const loadAssetSummary = useCallback(async () => {
    try {
      setMetricsError(null);
      const response = await getAssetDashboardSummary();
      if (response && typeof response === 'object') {
        setAssetSummary({
          totalAssets: Number(response.totalAssets) || 0,
          onHire: Number(response.onHire) || 0,
          offHire: Number(response.offHire) || 0,
          totalDownCount: Number(response.totalDownCount) || 0,
          outOfServiceAssets: Number(response.outOfServiceAssets) || 0,
          offSiteAssets: Number(response.offSiteAssets) || 0,
        });
      } else {
        throw new Error('Unexpected response when loading asset summary');
      }
    } catch (err) {
      console.error('Failed to load asset dashboard summary', err);
      setMetricsError('Unable to load asset totals right now.');
    }
  }, []);

  useEffect(() => {
    loadEvents();
  }, [loadEvents]);

  useEffect(() => {
    loadAssetSummary();
  }, [loadAssetSummary]);

  useEffect(() => {
    const interval = setInterval(() => {
      loadEvents();
    }, REFRESH_INTERVAL_MS);
    return () => clearInterval(interval);
  }, [loadEvents]);

  useEffect(() => {
    const interval = setInterval(() => {
      loadAssetSummary();
    }, REFRESH_INTERVAL_MS);
    return () => clearInterval(interval);
  }, [loadAssetSummary, REFRESH_INTERVAL_MS]);

  useEffect(() => {
    const interval = setInterval(() => {
      setTimestamp(new Date());
    }, REFRESH_INTERVAL_MS);
    return () => clearInterval(interval);
  }, []);

  const rows = useMemo(() => {
    return events.map((event) => {
      const assetParts = [];
      if (event?.asset?.assetNumber) assetParts.push(event.asset.assetNumber);
      if (event?.asset?.assetDescription) assetParts.push(event.asset.assetDescription);
      const assetLabel = assetParts.join(' - ') || '—';
      const badgeColor = event?.eventType?.iconColor || 'secondary-600';
      const eventTypeName = event?.eventType?.name || '—';
      return {
        id: event.id,
        asset: assetLabel,
        assetImage: event?.asset?.assetImage || null,
        timeDown: formatDateTimeOrDash(event?.startDate),
        estimatedUp: formatDateTimeOrDash(event?.plannedEndDate),
        downHours: getDownHours(event?.startDate, timestamp),
        reason: event?.name || '—',
        badgeColor,
        eventTypeName,
        assetDepartment: event.asset?.assetDepartment?.name,
        customer: event.asset?.customer?.name,
      };
    });
  }, [events, timestamp]);

  const breakdownCount = useMemo(() => {
    return rows.reduce((count, row) => {
      if (typeof row.eventTypeName !== 'string') return count;
      return row.eventTypeName.toLowerCase() === 'breakdown' ? count + 1 : count;
    }, 0);
  }, [rows]);

  const accidentDamageCount = useMemo(() => {
    return rows.reduce((count, row) => {
      if (typeof row.eventTypeName !== 'string') return count;
      return row.eventTypeName.toLowerCase() === 'accident damage' ? count + 1 : count;
    }, 0);
  }, [rows]);

  const standbyCount = useMemo(() => {
    return rows.reduce((count, row) => {
      if (typeof row.eventTypeName !== 'string') return count;
      return row.eventTypeName.toLowerCase() === 'standby' ? count + 1 : count;
    }, 0);
  }, [rows]);

  const totalDownCount = rows.length - standbyCount;

  const nonHire = assetSummary.totalAssets - assetSummary.onHire - assetSummary.offHire;
  const inServiceAssets = assetSummary.totalAssets - assetSummary.outOfServiceAssets - totalDownCount;

  let percentOnhire = 0;
  let percentOffhire = 0;
  let percentNonhire = 0;
  let percentDown = 0;
  let percentBreakdown = 0;
  let percentAccidentDamage = 0;
  let percentInservice = 0;

  if (assetSummary.totalAssets) { percentOnhire = parseInt((assetSummary.onHire / (assetSummary.totalAssets)) * 100, 10) };
  if (assetSummary.totalAssets) { percentOffhire = parseInt((assetSummary.offHire / (assetSummary.totalAssets)) * 100, 10) };
  if (assetSummary.totalAssets) { percentNonhire = parseInt((nonHire / (assetSummary.totalAssets)) * 100, 10) };
  if (assetSummary.totalAssets) { percentDown = parseInt((totalDownCount / assetSummary.totalAssets) * 100, 10) };
  if (totalDownCount) { percentBreakdown = parseInt((breakdownCount / (totalDownCount)) * 100, 10) };
  if (totalDownCount) { percentAccidentDamage = parseInt((accidentDamageCount / (totalDownCount)) * 100, 10) };
  if (assetSummary.totalAssets) { percentInservice = parseInt((inServiceAssets / (assetSummary.totalAssets)) * 100, 10) };

  const assetStrokeOffset = RADIAL_CIRCUMFERENCE - 1 * RADIAL_CIRCUMFERENCE;
  const downStrokeOffset = RADIAL_CIRCUMFERENCE - (percentDown / 100) * RADIAL_CIRCUMFERENCE;
  const breakStrokeOffset = RADIAL_CIRCUMFERENCE - (percentBreakdown / 100) * RADIAL_CIRCUMFERENCE;
  const accidentDamageStrokeOffset = RADIAL_CIRCUMFERENCE - (percentAccidentDamage / 100) * RADIAL_CIRCUMFERENCE;

  const tableRows = useMemo(() => {
    return rows.filter((row) => {
      const name = typeof row.eventTypeName === 'string' ? row.eventTypeName.toLowerCase() : '';
      return name === 'breakdown' || name === 'accident damage';
    });
  }, [rows]);

  const pages = useMemo(() => {
    if (!tableRows.length) return [];
    const chunks = [];
    for (let i = 0; i < tableRows.length; i += maxPageRecords) {
      chunks.push(tableRows.slice(i, i + maxPageRecords));
    }
    return chunks;
  }, [tableRows, maxPageRecords]);

  useEffect(() => {
    if (!pages.length) {
      setPageIndex(0);
      return;
    }

    setPageIndex((prev) => (prev < pages.length ? prev : 0));
  }, [pages.length]);

  useEffect(() => {
    if (pages.length <= 1) {
      setPageIndex(0);
      return () => { };
    }

    const interval = setInterval(() => {
      setPageIndex((prev) => (prev + 1) % pages.length);
    }, REFRESH_INTERVAL_MS);

    return () => clearInterval(interval);
  }, [pages.length]);

  const paginatedRows = pages[pageIndex] || [];

  return (
    <div className='card h-100'>

      <div className='card-body px-24 py-16'>
        {error && (
          <div className='alert alert-danger rounded-0 m-0'>{error}</div>
        )}
        {metricsError && (
          <div className='alert alert-danger rounded-0 mt-16'>{metricsError}</div>
        )}
        <div className='row g-16 mb-24'>
          <div className='col-12 col-md-3'>
            <div className='card-body px-24 py-0 h-100 d-flex flex-column justify-content-center border rounded-3'>
              <div className='d-flex justify-content-center'>
                <div className="position-relative">
                  <svg className="radial-progress" data-percentage="90" viewBox="0 0 80 80">
                    <circle className="incomplete" cx="40" cy="40" r="35"></circle>
                    <circle className="complete radial-progress-secondary" cx="40" cy="40" r="35" style={{ strokeDashoffset: `${assetStrokeOffset}px` }}></circle>
                    <text className="percentage" x="50%" y="57%" transform="matrix(0, 1, -1, 0, 80, 0)"></text>
                  </svg>
                  <div className="text-center position-absolute top-50 start-50 translate-middle">
                    <span className="fw-semibold text-primary-light text-lg">Total Assets</span>
                    <h4 className="mb-0">{assetSummary.totalAssets}</h4>
                    <span className="text-secondary-light text-sm m-0">{percentInservice}% In Service</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div className='col-12 col-md-3'>
            <div className='card-body px-24 py-0 h-100 d-flex flex-column justify-content-center border rounded-3'>
              <div className='d-flex justify-content-center'>
                <div className="position-relative">
                  <svg className="radial-progress" data-percentage="90" viewBox="0 0 80 80">
                    <circle className="incomplete" cx="40" cy="40" r="35"></circle>
                    <circle className="complete radial-progress-warning" cx="40" cy="40" r="35" style={{ strokeDashoffset: `${downStrokeOffset}px` }}></circle>
                    <text className="percentage" x="50%" y="57%" transform="matrix(0, 1, -1, 0, 80, 0)"></text>
                  </svg>
                  <div className="text-center position-absolute top-50 start-50 translate-middle">
                    <span className="fw-semibold text-primary-light text-lg">Down</span>
                    <h4 className="mb-0">{totalDownCount}</h4>
                    <span className="text-secondary-light text-sm m-0">{percentDown}% of Assets</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div className='col-12 col-md-3'>
            <div className='card-body px-24 py-0 h-100 d-flex flex-column justify-content-center border rounded-3'>
              <div className='d-flex justify-content-center'>
                <div className="position-relative">
                  <svg className="radial-progress" data-percentage="90" viewBox="0 0 80 80">
                    <circle className="incomplete" cx="40" cy="40" r="35"></circle>
                    <circle className="complete radial-progress-danger" cx="40" cy="40" r="35" style={{ strokeDashoffset: `${breakStrokeOffset}px` }}></circle>
                    <text className="percentage" x="50%" y="57%" transform="matrix(0, 1, -1, 0, 80, 0)"></text>
                  </svg>
                  <div className="text-center position-absolute top-50 start-50 translate-middle">
                    <span className="fw-semibold text-primary-light text-lg">Breakdowns</span>
                    <h4 className="mb-0">{breakdownCount}</h4>
                    <span className="text-secondary-light text-sm m-0">{percentBreakdown}% of Events</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div className='col-12 col-md-3'>
            <div className='card-body px-24 py-0 h-100 d-flex flex-column justify-content-center border rounded-3'>
              <div className='d-flex justify-content-center'>
                <div className="position-relative">
                  <svg className="radial-progress" data-percentage="90" viewBox="0 0 80 80">
                    <circle className="incomplete" cx="40" cy="40" r="35"></circle>
                    <circle className="complete radial-progress-warning" cx="40" cy="40" r="35" style={{ strokeDashoffset: `${accidentDamageStrokeOffset}px` }}></circle>
                    <text className="percentage" x="50%" y="57%" transform="matrix(0, 1, -1, 0, 80, 0)"></text>
                  </svg>
                  <div className="text-center position-absolute top-50 start-50 translate-middle">
                    <span className="fw-semibold text-primary-light text-lg">Accident Damage</span>
                    <h4 className="mb-0">{accidentDamageCount}</h4>
                    <span className="text-secondary-light text-sm m-0">{percentAccidentDamage}% of Events</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
          {/*{planthireEnabled && (
            <div className='col-12 col-md-4'>
              <div className='card-body px-24 py-0 h-100 d-flex flex-column border rounded-3'>
                <h3 className="text-xl mt-16 mb-24">Hire Fleet Status</h3>
                <div className="d-flex align-items-center justify-content-between gap-3 mb-16 pb-2">
                  <div className="d-flex align-items-center w-200-px">
                    <div className="flex-grow-1">
                      <h6 className="text-lg mb-0">On Hire</h6>
                    </div>
                  </div>
                  <div className="d-flex align-items-center gap-2 w-100">
                    <div className="w-100 max-w-90 ms-auto">
                      <div className="progress rounded-pill" role="progressbar" aria-label="On Hire" aria-valuenow="25" aria-valuemin="0" aria-valuemax="100">
                        <div className="progress-bar bg-success-600 rounded-pill" style={{ width: `${percentOnhire}%` }}></div>
                      </div>
                    </div>
                    <span class="text-secondary-light font-xs fw-semibold w-40-px text-xl text-end">{assetSummary.onHire.toLocaleString()}</span>
                  </div>
                </div>
                <div className="d-flex align-items-center justify-content-between gap-3 mb-16 pb-2">
                  <div className="d-flex align-items-center w-200-px">
                    <div className="flex-grow-1">
                      <h6 className="text-lg mb-0">Off Hire</h6>
                    </div>
                  </div>
                  <div className="d-flex align-items-center gap-2 w-100">
                    <div className="w-100 max-w-90 ms-auto">
                      <div className="progress rounded-pill" role="progressbar" aria-label="Off Hire" aria-valuenow="25" aria-valuemin="0" aria-valuemax="100">
                        <div className="progress-bar bg-warning-600 rounded-pill" style={{ width: `${percentOffhire}%` }}></div>
                      </div>
                    </div>
                    <span class="text-secondary-light font-xs fw-semibold w-40-px text-xl text-end">{assetSummary.offHire.toLocaleString()}</span>
                  </div>
                </div>
                <div className="d-flex align-items-center justify-content-between gap-3 mb-16 pb-2">
                  <div className="d-flex align-items-center w-200-px">
                    <div className="flex-grow-1">
                      <h6 className="text-lg mb-0">Non Hire</h6>
                    </div>
                  </div>
                  <div className="d-flex align-items-center gap-2 w-100">
                    <div className="w-100 max-w-90 ms-auto">
                      <div className="progress rounded-pill" role="progressbar" aria-label="Other Assets" aria-valuenow="25" aria-valuemin="0" aria-valuemax="100">
                        <div className="progress-bar bg-secondary-600 rounded-pill" style={{ width: `${percentNonhire}%` }}></div>
                      </div>
                    </div>
                    <span class="text-secondary-light font-xs fw-semibold w-40-px text-xl text-end">{nonHire.toLocaleString()}</span>
                  </div>
                </div>
              </div>
            </div>
          )}*/}
        </div>
        <div className='table-responsive'>
          <table className='table bordered-table mb-0 text-lg'>
            <thead>
              <tr>
                <th className="text-lg" scope='col'>Asset</th>
                <th className="text-lg" scope='col'>Site</th>
                <th className="text-lg" scope='col'>Time Down</th>
                <th className="text-lg" scope='col'>Estimated Up</th>
                <th className="text-lg" scope='col'>Down Hours</th>
                <th className="text-lg" scope='col'>Reason</th>
                <th className="text-lg" scope='col'>Type</th>
              </tr>
            </thead>
            <tbody>
              {!loading && tableRows.length === 0 && (
                <tr>
                  <td colSpan='7' className='text-center py-4'>No downtime events in progress.</td>
                </tr>
              )}
              {loading && (
                <tr>
                  <td colSpan='7' className='text-center py-4'>
                    <span className='spinner-border spinner-border-sm' role='status' aria-hidden='true' /> Loading...
                  </td>
                </tr>
              )}
              {!loading &&
                paginatedRows.map((row) => {
                  const imageUrl = row.assetImage
                    ? `${process.env.REACT_APP_SPACES_BASE}/${row.assetImage}`
                    : placeholder;

                  return (
                    <tr key={row.id}>
                      <td className="py-2">
                        <div className='d-flex align-items-center gap-36'>
                          <div
                            className='rounded-circle overflow-hidden flex-shrink-0 border border-secondary-200'
                            style={{ width: '45px', height: '45px' }}
                          >
                            <img
                              src={imageUrl}
                              alt={row.asset}
                              style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                            />
                          </div>
                          <span>{row.asset}</span>
                        </div>
                      </td>
                      <td className="py-8"> {`${row.assetDepartment} ${row.customer ? `(${row.customer})` : ""}`}</td>
                      <td className="py-8">{row.timeDown}</td>
                      <td className={`py-8 ${new Date(row.estimatedUp) < new Date() ? "text-danger" : ""}`}>
                        {row.estimatedUp}
                      </td>
                      <td className="py-8">{row.downHours}</td>
                      <td className="py-8">{row.reason}</td>
                      <td className="py-8">
                        <span className={`text-md badge bg-${row.badgeColor}-focus text-${row.badgeColor} px-12 py-8 fw-semibold`}>{row.eventTypeName}</span>
                      </td>
                    </tr>
                  );
                })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default DowntimeDashboardLayer;
